<template>
  <div class="row">
    <!-- Statistiques du transitaire -->
    <div class="col-lg-4 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-folder text-primary" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.totalDossiers }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Dossiers totaux</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-4 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-check text-success" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.dossiersValides }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Dossiers validés</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-4 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-time text-warning" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.dossiersEnValidation }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">En cours de validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Dossiers en Validation / Validés</h4>
          <router-link to="/transitaire-dossiers" class="btn btn-outline-primary btn-sm">
            Voir tous les dossiers
          </router-link>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>
          <div v-else-if="dossiers.length === 0" class="text-center py-4">
            <p class="text-muted">Aucun dossier en validation ou validé</p>
          </div>
          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Référence BESC</th>
                  <th>Destination</th>
                  <th>Statut</th>
                  <th>Date création</th>
                  <th>Date validation</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dossier in dossiers" :key="dossier.id">
                  <td class="fw-medium">{{ dossier.refbesc }}</td>
                  <td>{{ dossier.destination || '—' }}</td>
                  <td>
                    <span v-html="getStatusBadge(dossier.status)"></span>
                  </td>
                  <td>{{ format_date(dossier.created_at) }}</td>
                  <td>{{ format_date(dossier.validated_at) }}</td>
                  <td class="text-end">
                    <div class="dropdown">
                      <span class="badge text-white bg-primary fs-15 dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style="cursor: pointer;">
                        Actions
                        <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                      </span>
                      <ul class="dropdown-menu">
                        <li>
                          <router-link
                            v-if="dossier.id"
                            :to="{ name: 'ViewBesc', params: { id: dossier.id } }"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-eye lh-1 me-8 position-relative top-1"></i>
                            Détails
                          </router-link>
                        </li>
                        <li v-if="dossier.status === 'VALIDE'">
                          <a 
                            href="javascript:void(0);"
                            @click="telechargerPDF(dossier.id)"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-file-1 lh-1 me-8 position-relative top-1"></i>
                            Télécharger PDF
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import { error, format_date, success } from '@/utils/utils';

interface TransitaireStats {
  totalDossiers: number;
  dossiersValides: number;
  dossiersEnValidation: number;
}

interface Dossier {
  id: string;
  refbesc: string;
  client: string;
  destination: string;
  status: string;
  created_at: string;
  validated_at?: string;
}

export default defineComponent({
  name: 'TransitaireDashboard',
  setup() {
    const loading = ref(false);
    const stats = ref<TransitaireStats>({
      totalDossiers: 0,
      dossiersValides: 0,
      dossiersEnValidation: 0
    });
    const dossiers = ref<Dossier[]>([]);

    const loadTransitaireData = async () => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const statsResponse = await ApiService.get(`/transitaire/stats/${user.userId}`);
        stats.value = statsResponse.data.data;

        const dossiersResponse = await ApiService.get(
          `/transitaire/dossiers/${user.userId}?statuts=EN_VALIDATION,VALIDE&limit=10`
        );
        console.log("nouveauuuuuuuuuu",dossiersResponse);
        dossiers.value = dossiersResponse.data.data.data;

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        loading.value = false;
      }
    };

    const getStatusBadge = (status: string): string => {
      const badges = {
        'BROUILLON': '<span class="badge bg-secondary">Brouillon</span>',
        'SOUMIS': '<span class="badge bg-primary">Soumis</span>',
        'EN_VALIDATION': '<span class="badge bg-warning">En validation</span>',
        'VALIDE': '<span class="badge bg-success">Validé</span>',
        'REJETE': '<span class="badge bg-danger">Rejeté</span>'
      };
      return badges[status] || `<span class="badge bg-secondary">${status}</span>`;
    };

    const telechargerPDF = async (id: string) => {
      try {
        const response = await ApiService.getFile(`/besc/${id}/download-official`);

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `DossierOfficiel_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);

        success('Documment officiel téléchargé avec succès !');
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du téléchargement du PDF');
      }
    };


    onMounted(() => {
      loadTransitaireData();
    });

    return {
      loading,
      stats,
      dossiers,
      getStatusBadge,
      format_date,
      telechargerPDF
    };
  }
});
</script>

<style scoped>
.table th {
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  vertical-align: middle;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>